import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import visitLetterService from '../../services/visitLetterService';
import { useProject } from '../../context/ProjectContext';
import { getVisitLetterPdfErrorMessage } from '../../utils/visitLetterPdf';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import Pagination from '../Common/Pagination';
import VisitLetterFilters from './VisitLetterFilters';
import VisitLetterGrid, { SORT_KEYS } from './VisitLetterGrid';
import VisitLetterPreviewModal from './VisitLetterPreviewModal';
import '../Common/DataGrid.css';
import '../MailManagement/MailManagement.css';
import '../Visits/GenerateVisitLetterModal.css';
import './VisitLetterDownloads.css';

const TABS = [
  { key: 'visit', label: 'Visit Letter' },
  { key: 'followup', label: 'Follow-Up Letter' },
  { key: 'piconfirmation', label: 'PI Confirmation Letter' },
];

const VisitLetterDownloads = () => {
  const { currentProject } = useProject();
  const projectCode = currentProject && currentProject !== 'all'
    ? (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode ?? ''))
    : null;

  const [activeTab, setActiveTab] = useState('visit');
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [visitType, setVisitType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortBy, setSortBy] = useState('recordedOn');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [piVisitPage, setPiVisitPage] = useState(1);
  const [piFollowUpPage, setPiFollowUpPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [downloadingId, setDownloadingId] = useState(null);
  const [preview, setPreview] = useState({
    open: false,
    loading: false,
    content: null,
    siteId: null,
  });

  const fetchSites = useCallback(async () => {
    if (!projectCode) {
      setSites([]);
      return;
    }
    try {
      const url = `/sites?project=${encodeURIComponent(projectCode)}`;
      const response = await api.get(url);
      const sitesList = (response.data || [])
        .map((s) => {
          const siteCode = (s.siteCode ?? s.SiteCode ?? s.vSiteCode ?? '').toString().trim();
          const siteDescription = (s.siteDescription ?? s.SiteDescription ?? s.vSiteDescription ?? s.sitedescription ?? '').toString().trim();
          const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription;
          return {
            siteID: s.siteID ?? s.siteId ?? s.SiteNo ?? s.nSiteNo,
            siteCode,
            siteDescription,
            siteDisplay,
          };
        })
        .filter((s) => s.siteCode)
        .sort((a, b) => a.siteCode.localeCompare(b.siteCode));
      setSites(sitesList);
    } catch (err) {
      console.warn('Failed to load sites for visit letters:', err);
      setSites([]);
    }
  }, [projectCode]);

  const fetchList = useCallback(async () => {
    if (!projectCode) {
      setList([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await visitLetterService.getList({
        letterKind: activeTab,
        projectCode,
        siteId: siteId || undefined,
        visitType: visitType || undefined,
      });
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load letters');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, projectCode, siteId, visitType]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPage(1);
    setPiVisitPage(1);
    setPiFollowUpPage(1);
  }, [activeTab, siteId, visitType, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const filteredAndSorted = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    let result = list;
    if (searchLower) {
      result = result.filter((row) => {
        const visitId = (row.displayVisitCode ?? row.DisplayVisitCode ?? '').toString();
        const site = (row.siteCode ?? row.SiteCode ?? '').toString();
        const siteName = (row.siteName ?? row.SiteName ?? '').toString();
        const vt = (row.visitType ?? row.VisitType ?? '').toString();
        const status = (row.letterStatus ?? row.LetterStatus ?? '').toString();
        const flowType = (row.confirmationFlowType ?? row.ConfirmationFlowType ?? '').toString();
        const text = [visitId, site, siteName, vt, status, flowType].join(' ').toLowerCase();
        return text.includes(searchLower);
      });
    }
    const key = SORT_KEYS[sortBy] || SORT_KEYS.recordedOn;
    result = [...result].sort((a, b) => {
      let va = a[key] ?? a[key.charAt(0).toUpperCase() + key.slice(1)];
      let vb = b[key] ?? b[key.charAt(0).toUpperCase() + key.slice(1)];
      if (key === 'recordedOn' || key === 'visitDate') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return sortOrder === 'asc' ? va - vb : vb - va;
      }
      va = (va ?? '').toString().toLowerCase();
      vb = (vb ?? '').toString().toLowerCase();
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [list, searchTerm, sortBy, sortOrder]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAndSorted.slice(start, start + pageSize);
  }, [filteredAndSorted, page, pageSize]);

  const isPiTab = activeTab === 'piconfirmation';

  const visitPiRows = useMemo(() => {
    if (!isPiTab) return [];
    return filteredAndSorted.filter((row) => {
      const flowType = (row.confirmationFlowType ?? row.ConfirmationFlowType ?? '')
        .toString()
        .trim()
        .toLowerCase();
      return flowType !== 'followup';
    });
  }, [filteredAndSorted, isPiTab]);

  const followUpPiRows = useMemo(() => {
    if (!isPiTab) return [];
    return filteredAndSorted.filter((row) => {
      const flowType = (row.confirmationFlowType ?? row.ConfirmationFlowType ?? '')
        .toString()
        .trim()
        .toLowerCase();
      return flowType === 'followup';
    });
  }, [filteredAndSorted, isPiTab]);

  const visitPiPageRows = useMemo(() => {
    const start = (piVisitPage - 1) * pageSize;
    return visitPiRows.slice(start, start + pageSize);
  }, [visitPiRows, piVisitPage, pageSize]);

  const followUpPiPageRows = useMemo(() => {
    const start = (piFollowUpPage - 1) * pageSize;
    return followUpPiRows.slice(start, start + pageSize);
  }, [followUpPiRows, piFollowUpPage, pageSize]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    setPiVisitPage(1);
    setPiFollowUpPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getLetterId = (row) => row.letterId ?? row.LetterId;

  const resolveSiteIdForRow = (row) => {
    const code = String(row.siteCode ?? row.SiteCode ?? '').trim();
    if (code && sites.length > 0) {
      const match = sites.find((s) => String(s.siteCode ?? '').trim() === code);
      if (match) {
        const id = match.siteID ?? match.siteId;
        if (id != null && id !== '') return id;
      }
    }
    return siteId || null;
  };

  const handlePreview = async (row) => {
    const letterId = getLetterId(row);
    const resolvedSiteId = resolveSiteIdForRow(row);
    setPreview({ open: true, loading: true, content: null, siteId: resolvedSiteId });
    try {
      const content = await visitLetterService.getContent(activeTab, letterId);
      setPreview({
        open: true,
        loading: false,
        content,
        siteId: resolvedSiteId,
      });
    } catch (err) {
      setPreview({ open: false, loading: false, content: null, siteId: null });
      setToast(err?.response?.data?.error || err?.message || 'Failed to load letter preview');
    }
  };

  const handleDownload = async (row) => {
    const letterId = getLetterId(row);
    const resolvedSiteId = resolveSiteIdForRow(row);
    setDownloadingId(letterId);
    try {
      await visitLetterService.downloadPdf(activeTab, letterId, row, {
        projectCode,
        siteId: resolvedSiteId,
      });
      setToast('PDF downloaded successfully.');
    } catch (err) {
      const message = await getVisitLetterPdfErrorMessage(err);
      setToast(message || err?.message || 'PDF download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const gridCommonProps = {
    letterKind: activeTab,
    loading,
    sortBy,
    sortOrder,
    onSort: handleSort,
    onPreview: handlePreview,
    onDownload: handleDownload,
    downloadingId,
  };

  const emptyProjectMessage = !projectCode
    ? 'Please select a project to view and download visit letters.'
    : null;

  return (
    <div className="visit-letter-downloads-page mail-management-page">
      {toast && <Alert type="info" message={toast} onClose={() => setToast(null)} />}
      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {emptyProjectMessage && <Alert type="info" message={emptyProjectMessage} autoClose={false} />}

      <div className="mail-management-tabs visit-letter-tabs">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`tab-button ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        <VisitLetterFilters
          sites={sites}
          siteId={siteId}
          visitType={visitType}
          searchTerm={searchTerm}
          onSiteChange={setSiteId}
          onVisitTypeChange={setVisitType}
          onSearchChange={setSearchTerm}
          disabled={!projectCode || loading}
        />

        <div className="visit-letter-grid-wrap">
          {loading && list.length === 0 ? (
            <Spinner />
          ) : (
            <>
              <div className="data-grid-toolbar">
                <div className="data-grid-controls">
                  <div className="data-grid-filter">
                    <span className="data-grid-filter-label">Page size</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="form-select data-grid-filter-select data-grid-page-size"
                    >
                      <option value={5}>5 items</option>
                      <option value={10}>10 items</option>
                      <option value={25}>25 items</option>
                      <option value={50}>50 items</option>
                    </select>
                  </div>
                </div>
              </div>
              {isPiTab ? (
                <>
                  <section className="visit-letter-pi-section">
                    <h3 className="visit-letter-pi-section-title">Visit PI Confirmation</h3>
                    <VisitLetterGrid
                      rows={visitPiPageRows}
                      emptyMessage="No visit PI confirmation letters found for selected filters."
                      {...gridCommonProps}
                    />
                    {!loading && visitPiRows.length > 0 && (
                      <Pagination
                        page={piVisitPage}
                        pageSize={pageSize}
                        totalItems={visitPiRows.length}
                        onPageChange={setPiVisitPage}
                        onPageSizeChange={handlePageSizeChange}
                        showPageSize={false}
                      />
                    )}
                  </section>
                  <section className="visit-letter-pi-section">
                    <h3 className="visit-letter-pi-section-title">Follow-Up PI Confirmation</h3>
                    <VisitLetterGrid
                      rows={followUpPiPageRows}
                      emptyMessage="No follow-up PI confirmation letters found for selected filters."
                      {...gridCommonProps}
                    />
                    {!loading && followUpPiRows.length > 0 && (
                      <Pagination
                        page={piFollowUpPage}
                        pageSize={pageSize}
                        totalItems={followUpPiRows.length}
                        onPageChange={setPiFollowUpPage}
                        onPageSizeChange={handlePageSizeChange}
                        showPageSize={false}
                      />
                    )}
                  </section>
                </>
              ) : (
                <>
                  <VisitLetterGrid
                    rows={pageRows}
                    {...gridCommonProps}
                  />
                  {!loading && filteredAndSorted.length > 0 && (
                    <Pagination
                      page={page}
                      pageSize={pageSize}
                      totalItems={filteredAndSorted.length}
                      onPageChange={setPage}
                      onPageSizeChange={handlePageSizeChange}
                      showPageSize={false}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <VisitLetterPreviewModal
        open={preview.open}
        onClose={() => setPreview({ open: false, loading: false, content: null, siteId: null })}
        loading={preview.loading}
        content={preview.content}
        letterKind={activeTab}
        projectCode={projectCode}
        siteId={preview.siteId}
      />
    </div>
  );
};

export default VisitLetterDownloads;
